import { collection, getDocs, doc, getDoc, updateDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

/**
 * Batch Repair Service
 *
 * Scans all stock transfers and cross-checks them against the inventory batch
 * arrays. Any transfer line whose batchId is missing from its inventory item's
 * batches array is flagged as a "missing batch" and can be backfilled.
 */

// ─── Step 1: Scan & detect ────────────────────────────────────────────────────

/**
 * Scans all stock transfers and returns a list of transfer lines whose batch
 * is missing from the corresponding inventory document.
 *
 * @returns {{ success: boolean, missing: MissingBatch[], error?: string }}
 *
 * MissingBatch shape:
 * {
 *   transferId, transferNumber, transferDate, supplier,
 *   inventoryItemId, itemName, itemCategory, itemCode,
 *   batchId, qty, costPrice, retailPrice, wholesalePrice, discount,
 *   grnId, grnNumber
 * }
 */
export const scanMissingBatches = async () => {
  try {
    // Load all transfers
    const transferSnap = await getDocs(
      query(collection(db, 'stockTransfers'), orderBy('createdAt', 'desc'))
    );
    const transfers = transferSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Load all inventory items into a map for fast lookup
    const invSnap = await getDocs(collection(db, 'inventory'));
    const inventoryMap = new Map();
    invSnap.docs.forEach((d) => {
      inventoryMap.set(d.id, { id: d.id, ...d.data() });
    });

    const missing = [];

    for (const transfer of transfers) {
      if (!Array.isArray(transfer.items)) continue;

      for (const line of transfer.items) {
        const invItemId = line.inventoryItemId;
        if (!invItemId) continue;

        const invItem = inventoryMap.get(invItemId);
        if (!invItem) {
          // Inventory item doesn't exist at all — flag it
          missing.push({
            transferId: transfer.id,
            transferNumber: transfer.transferNumber || '',
            transferDate: transfer.transferDate || '',
            supplier: transfer.supplier || 'N/A',
            inventoryItemId: invItemId,
            itemName: line.name,
            itemCategory: line.category || '',
            itemCode: line.itemCode || '',
            batchId: line.batchId || null,
            qty: line.qty,
            costPrice: line.costPrice,
            retailPrice: line.retailPrice || 0,
            wholesalePrice: line.wholesalePrice || 0,
            discount: line.discount || 0,
            grnId: transfer.grnId || '',
            grnNumber: transfer.grnNumber || '',
            reason: 'inventory_item_missing',
          });
          continue;
        }

        const batches = invItem.batches || [];

        // If this transfer line has a recorded batchId, look for it
        if (line.batchId) {
          const exists = batches.some((b) => b.batchId === line.batchId);
          if (!exists) {
            missing.push({
              transferId: transfer.id,
              transferNumber: transfer.transferNumber || '',
              transferDate: transfer.transferDate || '',
              supplier: transfer.supplier || 'N/A',
              inventoryItemId: invItemId,
              itemName: line.name,
              itemCategory: line.category || '',
              itemCode: line.itemCode || '',
              batchId: line.batchId,
              qty: line.qty,
              costPrice: line.costPrice,
              retailPrice: line.retailPrice || 0,
              wholesalePrice: line.wholesalePrice || 0,
              discount: line.discount || 0,
              grnId: transfer.grnId || '',
              grnNumber: transfer.grnNumber || '',
              reason: 'batch_id_not_found',
            });
          }
        } else {
          // No batchId recorded on the transfer line — check if there is ANY batch
          // that references this transfer number; if none, flag it
          const hasTransferBatch = batches.some(
            (b) => b.transferNumber === transfer.transferNumber
          );
          if (!hasTransferBatch) {
            missing.push({
              transferId: transfer.id,
              transferNumber: transfer.transferNumber || '',
              transferDate: transfer.transferDate || '',
              supplier: transfer.supplier || 'N/A',
              inventoryItemId: invItemId,
              itemName: line.name,
              itemCategory: line.category || '',
              itemCode: line.itemCode || '',
              batchId: null,
              qty: line.qty,
              costPrice: line.costPrice,
              retailPrice: line.retailPrice || 0,
              wholesalePrice: line.wholesalePrice || 0,
              discount: line.discount || 0,
              grnId: transfer.grnId || '',
              grnNumber: transfer.grnNumber || '',
              reason: 'no_batch_id_and_no_transfer_batch',
            });
          }
        }
      }
    }

    return { success: true, missing };
  } catch (error) {
    console.error('scanMissingBatches error:', error);
    return { success: false, error: error.message, missing: [] };
  }
};

// ─── Step 2: Backfill ─────────────────────────────────────────────────────────

/**
 * Writes the missing batches back into their inventory documents.
 * Each missing entry gets a new batch appended to the item's batches array,
 * and stock / averageCost are recalculated.
 *
 * @param {MissingBatch[]} missingBatches - list returned by scanMissingBatches
 * @returns {{ success: boolean, repaired: number, failed: string[], error?: string }}
 */
export const repairMissingBatches = async (missingBatches) => {
  const repaired = [];
  const failed = [];

  // Group by inventoryItemId so we do one write per item even if multiple batches
  // are missing for the same item
  const grouped = new Map();
  for (const entry of missingBatches) {
    if (!grouped.has(entry.inventoryItemId)) {
      grouped.set(entry.inventoryItemId, []);
    }
    grouped.get(entry.inventoryItemId).push(entry);
  }

  for (const [invItemId, entries] of grouped.entries()) {
    try {
      const itemRef = doc(db, 'inventory', invItemId);
      const itemSnap = await getDoc(itemRef);

      // If the inventory document doesn't exist yet, we can't repair it here —
      // re-running the stock transfer is needed. Skip and flag.
      if (!itemSnap.exists()) {
        entries.forEach((e) => failed.push(`${e.itemName} (inventory item not found)`));
        continue;
      }

      const itemData = itemSnap.data();
      const existingBatches = itemData.batches || [];

      // Build set of existing batchIds so we don't double-insert
      const existingIds = new Set(existingBatches.map((b) => b.batchId));

      const batchesToAdd = [];
      for (const entry of entries) {
        // Stable batchId: reuse the original if it was recorded, else generate one
        const batchId =
          entry.batchId && !existingIds.has(entry.batchId)
            ? entry.batchId
            : `batch-repair-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        if (existingIds.has(batchId)) {
          // Already present (possible if scan ran twice) — skip silently
          repaired.push(entry.itemName);
          continue;
        }

        batchesToAdd.push({
          batchId,
          purchaseInvoiceId: entry.grnId || 'N/A',
          purchaseInvoiceNumber: entry.grnNumber || 'N/A',
          transferNumber: entry.transferNumber || 'N/A',
          purchaseDate: entry.transferDate || new Date().toISOString().split('T')[0],
          costPrice: entry.costPrice,
          retailPrice: entry.retailPrice || 0,
          wholesalePrice: entry.wholesalePrice || 0,
          discount: entry.discount || 0,
          quantityPurchased: entry.qty,
          quantityRemaining: entry.qty,
          supplier: entry.supplier || 'N/A',
          source: 'stock_transfer',
          repairedAt: new Date().toISOString(), // audit marker
          createdAt: entry.transferDate
            ? new Date(entry.transferDate).toISOString()
            : new Date().toISOString(),
        });

        existingIds.add(batchId);
      }

      if (batchesToAdd.length === 0) continue;

      const allBatches = [...existingBatches, ...batchesToAdd];

      // Recalculate stock and average cost from all batches
      const totalStock = allBatches.reduce((s, b) => s + (b.quantityRemaining || 0), 0);
      const totalCost = allBatches.reduce(
        (s, b) => s + (b.costPrice || 0) * (b.quantityRemaining || 0),
        0
      );
      const averageCost = totalStock > 0 ? totalCost / totalStock : itemData.costPrice || 0;

      await updateDoc(itemRef, {
        batches: allBatches,
        stock: totalStock,
        averageCost,
        lastCostPrice: batchesToAdd[batchesToAdd.length - 1].costPrice,
        updatedAt: serverTimestamp(),
      });

      batchesToAdd.forEach(() => repaired.push(entries[0].itemName));
    } catch (err) {
      console.error(`repairMissingBatches error for ${invItemId}:`, err);
      entries.forEach((e) => failed.push(`${e.itemName}: ${err.message}`));
    }
  }

  return {
    success: failed.length === 0,
    repaired: repaired.length,
    failed,
  };
};
